import React, { useState } from 'react';
import { Button, FileUpload, Alert, Modal, ModalVariant } from '@patternfly/react-core';
import { exportModelCustomization, importModelCustomization, importModelCustomizationFailure, StoreState } from '../../store/reducers';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { readModelCustomizationJsonFile } from '../../store/model-customization-helpers';


const ConfigurationImportExport = () => {
    const appClient = useSelector((state: StoreState) => state.currentAppClient);  
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [isImportingCompletedTextVisible, setIsImportingCompletedTextVisible] = useState(false);
    const [showCustomizationImportFile, setShowCustomizationImportFile] = useState<File | undefined>(undefined);
    const [showConfirmFileImport, setShowConfirmFileImport] = useState(false);

    const importModelCustomizationFailureSelect = useSelector((state: StoreState) => state.customizationImportExportError);

    const handleImportFileChange = async (
        file: File,
    ) => {
        await handleImportUserData(file);
        setShowConfirmFileImport(false);

    };

    const handleConfirmImportFileChange = async (
        _event: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLElement>,
        file: File,
    ) => {
        setShowCustomizationImportFile(file);
        setShowConfirmFileImport(true);
    }

    const handleClear = (_event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setShowCustomizationImportFile(undefined);
    };

    const handleExportUserData = async () => {
        if (!appClient) {
            return;
        }
        dispatch(exportModelCustomization(appClient));
    };

    const handleImportUserData = async (json: any) => {
        try {
            const readFile = await readModelCustomizationJsonFile(json);
            dispatch(importModelCustomization(readFile));
            setIsImportingCompletedTextVisible(true);
        } catch (error) {
            setIsImportingCompletedTextVisible(false);
            dispatch(importModelCustomizationFailure('Error importing configuration. Please ensure the imported data is valid.'));
        }
    };

    return (
        <>
            {isImportingCompletedTextVisible && (
                <div className="configuration-reset-completed-box">
                    <Alert variant="info" isInline title={t('settings.importConfiguration.success')} />
                </div>
            )}

            {importModelCustomizationFailureSelect && (
                <div className="configuration-reset-completed-box">
                    <Alert
                        variant="danger"
                        isInline
                        title={importModelCustomizationFailureSelect}
                    />
                </div>
            )}

            {appClient && (
                <>
                    <div className="setting-item">
                        <span className="label">{t('settings.importConfiguration')}</span>
                        <span className="value">
                            <FileUpload
                                id="daemon-config-file"
                                filename={showCustomizationImportFile?.name || ''}
                                filenamePlaceholder={t('settings.importConfiguration.file.instructions')}
                                title={t('settings.importConfiguration.file.instructions')}
                                onFileInputChange={handleConfirmImportFileChange}
                                onClearClick={handleClear}
                                browseButtonText={t('settings.importConfiguration.file.browse')}
                                clearButtonText={t('settings.importConfiguration.file.clear')}
                            />
                        </span>
                    </div>

                    <div className="setting-item">
                        <span className="label">{t('settings.exportConfiguration')}</span>
                        <span className="value">
                            <Button variant="secondary" onClick={handleExportUserData}>{t('settings.exportConfiguration.button')}</Button>
                        </span>
                    </div>
                </>
            )}

            <Modal
                variant={ModalVariant.small}
                title={t('settings.confirmImport.title')}
                isOpen={showConfirmFileImport && showCustomizationImportFile !== undefined}
                showClose
                onClose={() => setShowConfirmFileImport(false)}
                actions={[
                    <Button key="confirmImport" variant="primary" onClick={() => handleImportFileChange(showCustomizationImportFile!)}>{t('common.ok')}</Button>,
                    <Button key="cancelImport" variant="secondary" onClick={() => setShowConfirmFileImport(false)}>{t('common.cancel')}</Button>
                ]}
            >{t('settings.confirmImport.message')}
            </Modal>
        </>
    );
};

export default ConfigurationImportExport;
